import * as Yup from "yup";
import {
  emailValidationRegex,
  phoneNumberRegex,
  faxNumberRegex,
  websiteUrlRegex,
  zipCodeRegex,
} from "../../../constants/regexStrings";
import phoneValidation from "../../../common/components/phone-number-input-component/phone-validation";
import { isOnlyCountryCode } from "../../../utils/utils";
import { RELATIONSHIP_OPTIONS } from "../../../constants/leadDropdown";

export const initialValues = {
  title: "",
  company: "",
  firstName: "",
  lastName: "",
  email: "",
  secondaryEmail: "",
  fax: "",
  website: "",
  numEmployees: "",
  annualRevenue: "",
  interestedProduct: "",
  socialLeadId: "",
  streetAddress: "",
  description: "",
  city: "",
  state: "",
  country: "",
  zipcode: "",
  primaryPhoneNumber: "",
  secondaryPhoneNumber: "",
  imageUrl: null,
  industry: "",
  designation: "",
  deathDate: null,
  contactPerson: "",
  contactPersonPhoneNumber: "",
  relationship: "",
};

const FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/svg+xml",
];
export const validationSchema = (isEditMode: boolean) =>
  Yup.object().shape({
    company: Yup.string()
      .required("Company cannot be empty")
      .max(150, "Email cannot be more than 150 characters"),
    title: Yup.string().max(50, "Title cannot be more than 50 characters"),
    firstName: Yup.string().required("First Name cannot be empty"),
    lastName: isEditMode
      ? Yup.string()
      : Yup.string().required("Last Name cannot be empty"),
    email: Yup.string()
      .matches(emailValidationRegex, "Please enter a valid Email address")
      .required("Email cannot be empty")
      .max(150, "Email cannot be more than 150 characters"),
    secondaryEmail: Yup.string()
      .matches(emailValidationRegex, "Please enter a valid Email address")
      .max(150, "Secondary Email cannot be more than 150 characters")
      .nullable()
      .test(
        "notEmptyWhenProvided",
        "Secondary Email cannot be same as Primary Email",
        function (value) {
          const { email } = this.parent;
          if (value && email && email === value) {
            return false;
          }

          return true;
        },
      ),
    primaryPhoneNumber: Yup.string()
      .test(
        "is-valid-phone",
        "Please enter a valid phone number",
        function (value: any) {
          if (isOnlyCountryCode(value)) {
            return true;
          }
          return phoneValidation(value);
        },
      )
      .nullable(),

    secondaryPhoneNumber: Yup.string()
      .test(
        "is-valid-phone",
        "Please enter a valid phone number",
        function (value: any) {
          if (isOnlyCountryCode(value)) {
            return true;
          }
          return phoneValidation(value);
        },
      )
      .nullable()
      .test(
        "not-same-as-primary",
        "Secondary Phone Number cannot be same as Primary Phone Number",
        function (value) {
          const { primaryPhoneNumber } = this.parent;

          // Skip the comparison if both primary and secondary are country codes
          if (
            isOnlyCountryCode(primaryPhoneNumber) &&
            isOnlyCountryCode(value)
          ) {
            return true; // Don't compare country codes only
          }

          if (value && primaryPhoneNumber && value === primaryPhoneNumber) {
            return false;
          }
          return true;
        },
      ),
    website: Yup.string()
      .matches(websiteUrlRegex, "Enter a valid website URL")
      .nullable()
      .max(150, "Website cannot be more than 150 characters"),
    socialLeadId: Yup.string()
      .nullable()
      .max(100, "Social lead id cannot be more than 100 characters"),
    numEmployees: Yup.number()
      .min(1, "Employee count must be greater than 0")
      .max(9999999, "Employee count cannot exceed 10 digits") // 10 digits max
      .nullable(),
    annualRevenue: Yup.number()
      .min(1, "Annual Revenue must be greater than 0")
      .nullable(),
    fax: Yup.string()
      .nullable()
      .test("is-valid-fax", "Invalid fax number", (value) => {
        if (!value || isOnlyCountryCode(value)) {
          return true; // skip validation
        }
        return faxNumberRegex.test(value);
      }),
    imageUrl: Yup.mixed()
      .nullable()
      .test(
        "fileFormat",
        "Only JPG, JPEG, PNG, SVG and WEBP files are allowed",
        (value: any) => {
          return !value || SUPPORTED_FORMATS.includes(value?.type);
        },
      )
      .test("fileSize", "Please upload max 10MB file", (value: any) => {
        return !value || value?.size <= FILE_SIZE;
      }),

    streetAddress: Yup.string()
      .nullable()
      .max(100, "Street cannot be more than 100 characters"),
    city: Yup.string()
      .max(100, "City name cannot be more than 100 characters")
      .nullable(),

    state: Yup.string()
      .max(100, "State name cannot be more than 100 characters")
      .nullable(),
    country: Yup.string()
      .max(100, "Country name cannot be more than 100 characters")
      .nullable(),
    zipcode: Yup.string().matches(zipCodeRegex, "Invalid Zipcode").nullable(),
    description: Yup.string()
      .max(150, "Description cannot be more than 150 characters")
      .nullable(),
    deathDate: Yup.date()
      .nullable()
      .typeError("Invalid death date")
      .max(new Date(), "Death date cannot be in the future"),
    relationship: Yup.string()
      .nullable()
      .oneOf(
        RELATIONSHIP_OPTIONS.map((opt) => opt.value),
        "Please select a valid relationship",
      ),
  });
